import { ref, watch } from 'vue';

import { defineStore } from 'pinia';

import { themeConfig } from '@themeConfig';

import { isDarkPreferred } from '@/@core/composable/useThemeConfig.js';
import { getItemFromLocalStorage, setItemToLocalStorage } from '@/helpers/localStorageServices';
import { camelCaseToKebabCase } from '@/helpers/stringFormatter';
import { runThemeTransition } from '@/helpers/viewTransition';

const THEMES = 'themes';
const LIGHT_THEME = 'light-theme';
const DARK_THEME = 'dark-theme';
const USER_THEME = 'user-theme';

export const useThemeStore = defineStore('theme', () => {
  // No explicit choice yet → follow the OS preference (prefers-color-scheme).
  const userTheme = ref(
    getItemFromLocalStorage(USER_THEME) || (isDarkPreferred.value ? DARK_THEME : LIGHT_THEME),
  );

  // Apply a theme to both layers WITHOUT persisting: WisePPC layer = documentElement classes
  // (drives --wp-* tokens + Element Plus), core layer = themeConfig.app.theme (drives Vuetify +
  // the Switcher icon). Writing the core ref directly avoids the persisting computed setter, so
  // the "follow OS" mode is preserved until the user makes an explicit choice.
  const applyTheme = (paletteColor) => {
    const isDark = paletteColor === DARK_THEME;
    userTheme.value = paletteColor;

    document.documentElement.classList.toggle(DARK_THEME, isDark);
    document.documentElement.classList.toggle(LIGHT_THEME, !isDark);
    // Element Plus dark mode
    document.documentElement.classList.toggle('dark', isDark);

    // Keep the core/Vuetify layer in sync (Switcher icon, Vuetify components).
    themeConfig.app.theme.value = isDark ? 'dark' : 'light';
  };

  // Persist the choice (marks it as explicit) and apply it with the circular
  // View Transition. This is the only user-initiated theme switch, so it owns
  // the animation; applyTheme() stays instant for init / OS-preference sync.
  const setTheme = (paletteColor) => {
    setItemToLocalStorage(USER_THEME, paletteColor);
    // Keep the core/Vuetify theme key (read by @core on startup) in sync — the Switcher
    // used to write this; now the store owns the whole switch.
    localStorage.setItem(`${themeConfig.app.title}-theme`, paletteColor === DARK_THEME ? 'dark' : 'light');
    runThemeTransition(() => applyTheme(paletteColor), { toDark: paletteColor === DARK_THEME });
  };

  const applyThemeColors = (themes, saveTheme) => {
    const applyColorObject = (colorObject, propertyName) => {
      if (Object.entries(colorObject) && Object.entries(colorObject).length) {
        Object.entries(colorObject).forEach(([key, value]) => {
          document.documentElement.style.setProperty(
            `--${propertyName}-${camelCaseToKebabCase(key)}`,
            value,
          );
        });
      }
    };

    applyColorObject(themes.light, 'white');
    applyColorObject(themes.dark, 'dark');

    if (saveTheme) {
      setItemToLocalStorage(THEMES, themes);
    }
  };

  const toggleTheme = () => {
    // Self-contained: derive the next theme from the store's own state instead of the
    // Switcher pre-flipping the core `theme` ref (which desynced Vuetify — see Switcher).
    setTheme(userTheme.value === DARK_THEME ? LIGHT_THEME : DARK_THEME);
  };

  const initializeTheme = () => {
    const defaultAccentColor = import.meta.env.VITE_DEFAULT_ACCENT_COLOR;

    document.documentElement.style.setProperty('--dark-accent', defaultAccentColor);
    document.documentElement.style.setProperty('--white-accent', defaultAccentColor);

    applyTheme(userTheme.value);

    const savedTheme = getItemFromLocalStorage(THEMES);
    if (savedTheme) {
      applyThemeColors(savedTheme, false);
    }

    // Live-follow the OS theme until the user makes an explicit choice (which writes USER_THEME).
    watch(isDarkPreferred, (prefersDark) => {
      if (!getItemFromLocalStorage(USER_THEME)) {
        applyTheme(prefersDark ? DARK_THEME : LIGHT_THEME);
      }
    });
  };

  return {
    DARK_THEME,
    LIGHT_THEME,
    applyThemeColors,
    userTheme,
    toggleTheme,
    initializeTheme,
  };
});
